import WhiteLogo from "../../components/ui/WhiteLogo";
import React, {useContext, useEffect, useMemo, useRef, useState} from "react";
import { routesPath } from "./routesPath";
import { Link } from "react-router-dom";
import SettingIcon from "../../assets/icons/icon-settings.svg";
import PublicIcon from "../../assets/icons/icon-public.svg";

import ClockIcon from "../../assets/icons/clock-dark.svg";
import {useOidc} from "@axa-fr/react-oidc";
import {triggerLogout} from "@/api/authLogout";
import LogoutIcon from "@mui/icons-material/Logout";
import {UserContext, useAuth} from "@/context/user";
import {
    useRestrictedSettings,
    useRestrictedUserManagement,
} from "../../restrictions";
import {useTimeZoneSelectContext} from "@/providers/TimeZoneSelectProvider";
import CustomAlert from "./BannerAlert";

import {CountrySelectSingle} from "@/components/form-v2/searchable-select/CountrySelectSingle";
import BasicSelect from "../../components/form-v2/basic-select";
import useOnClickOutside from "../../hooks/useOnClickOutside";
import {getTimezonesForCountryWithOverride} from "../../util/tz";
import { apiV2 } from "@/api/axiosConfig";
import { Client } from "@/types/userManagement";
import {useClientModules} from "@/context/clientModules";



function Header() {

    const {developerAccess} = useAuth();
  const userContext = useContext(UserContext)!;
  const auroraModules = userContext!.role.auroraModules;
  const { isAuthenticated } = useOidc();
  const accessUserManagement = useRestrictedUserManagement();
  const firstSettingsLink = useRestrictedSettings()[0];
  const handleLogout = async () => {
    isAuthenticated && (await triggerLogout());
  };
  const userFirstNameInitial = userContext.profile.firstName.split("")[0];
  const userLastNameInitial = userContext.profile.lastName.split("")[0];
  const [headerStyle, setHeaderStyle] = useState<string>(
    "linear-gradient(to right, #2D166F 50%, #007584)"
  );
  const previewActiveRef = useRef(false);

  const toPlatformStyle = (colours?: Client["platformColours"]) => {
    if (!colours) return "";
    if (colours.mode === "gradient" && colours.primary && colours.secondary) {
      const center = Math.max(0, Math.min(100, colours.gradientCenter ?? 75));
      const blend = Math.max(0, Math.min(100, colours.gradientBlend ?? 50));
      const start = Math.max(0, Math.min(100, center - blend / 2));
      const end = Math.max(0, Math.min(100, center + blend / 2));
      return `linear-gradient(to right, ${colours.primary} 0%, ${colours.primary} ${start}%, ${colours.secondary} ${end}%, ${colours.secondary} 100%)`;
    }
    if (colours.mode === "single" && colours.colour) {
      return colours.colour;
    }
    return "";
  };

    const timeZoneSelect = useTimeZoneSelectContext();
    const { hasModule } = useClientModules();

    const getTimezoneOptions = (countryISO: string) => {
        return getTimezonesForCountryWithOverride(countryISO)?.map((tz) => ({
            label: tz.name,
            value: tz.name
        })) || []
    }

    const [tzCountry, setTZCountry] = useState<undefined | string>()
    const tzDropdownRef = useRef(null);
    useOnClickOutside(tzDropdownRef, () => setShowTZSelect(false));
    const [showTZSelect, setShowTZSelect] = useState(false)

    const managementLink = useMemo(() => {
        if (userContext.role.auroraAccessLevel == "all") return "/management/clients"
        if (userContext.role.auroraAccessLevel == "client") return "/management/departments"
        if (userContext.role.auroraAccessLevel == "department") return "/management/departments"

        return "#"
    }, [userContext.role.auroraAccessLevel]);

    useEffect(() => {
      const fetchColours = async () => {
        if (!userContext.metadata.clientID) return;

        try {
          const res = await apiV2.get<Client>(`clients/${userContext.metadata.clientID}`);
          const colours = res.data.platformColours;

          if (previewActiveRef.current) {
            return;
          }
          const style = toPlatformStyle(colours);
          if (style) {
            setHeaderStyle(style);
          }
        } catch (err) {
          console.error("Failed to load platform colours", err);
        }
      };

      fetchColours();
    }, [userContext.metadata.clientID]);

    useEffect(() => {
      const handlePreview = (evt: Event) => {
        const detail = (evt as CustomEvent<Client["platformColours"]>).detail;
        const style = toPlatformStyle(detail);
        if (!style) return;
        previewActiveRef.current = true;
        setHeaderStyle(style);
      };

      window.addEventListener("platform-colours-preview", handlePreview);
      return () => window.removeEventListener("platform-colours-preview", handlePreview);
    }, []);


  return (
    <header className="fixed top-0 z-[100] w-screen">
      <CustomAlert />
      <div className="h-28 w-full px-6 flex"
        style={{ background: headerStyle }}
      >
        <div className="h-full">
          <WhiteLogo />
        </div>

                <nav className="flex items-end w-full ml-16">
                    <ul className="flex flex-row text-white justify-start w-full">
                        {routesPath.map((route, i) => {
                            if (route.superadmin) {
                                if (userContext.role.auroraAccessLevel !== "all") return null;
                                return (
                                    <li className="" key={route.slug} id={`route-${route.slug}`}>
                                        <Link className={"px-4 border-l"} to={route.path}>
                                            <span className="inline-block h-12 hover:border-b-4">
                                                {route.title}
                                            </span>
                                        </Link>
                                    </li>
                                );
                            }
                            if (route.title == "Siren"){
                                if(!hasModule("siren")) return null;

                                return (
                                    <li className="" key={route.slug} id={`route-${route.slug}`}>
                                        <Link className={"px-4 border-r"} to={route.path}>
                                                <span className="inline-block h-12 hover:border-b-4">
                                                    {route.title}
                                                </span>
                                        </Link>
                                    </li>
                                );
                            }
                            else if (
                                auroraModules?.includes("all") ||
                                auroraModules?.includes(route.slug)
                            ) {


                                if (route.slug === "e_learning") {
                                    return (
                                        <li key={route.slug}>
                                            <a
                                                href={route.path}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="px-4"
                                            >
                                                {route.title}
                                            </a>
                                        </li>
                                    );
                                } else {
                                    return (
                                        <li className="" key={route.slug} id={`route-${route.slug}`}>
                                            <Link className={"px-4 border-r"} to={route.path}>
                                                <span className="inline-block h-12 hover:border-b-4">
                                                    {route.title}
                                                </span>
                                            </Link>
                                        </li>
                                    );
                                }
                            }
                        })}
                    </ul>
                </nav>

                {/**
                 * USER MENU
                 */}

                <div className="flex text-white gap-4 justify-center items-center">
                    <div className={"relative"}>
                        <button
                            onClick={() => setShowTZSelect(true)}
                            className="w-10 h-10 rounded-md flex items-center justify-center bg-white"
                        >
                            <img src={ClockIcon} alt=""/>
                        </button>
                        {showTZSelect && <div ref={tzDropdownRef}
                                              className={"absolute bg-white z-[1000] left-[-7em] top-[3em] w-[16em] p-4 rounded shadow text-grey flex flex-col gap-4"}>
                            <CountrySelectSingle value={timeZoneSelect.countryISO} setValue={(v) => {
                                if (v) {
                                    timeZoneSelect.setCountryISO(v);
                                }
                            }} label={"Country"}/>
                            {timeZoneSelect.countryISO &&
                                <BasicSelect label={"Timezone"} options={getTimezoneOptions(timeZoneSelect.countryISO)}
                                             value={timeZoneSelect.timeZone}
                                             onChange={(v) => {
                                                 if (v) {
                                                     timeZoneSelect.setTimeZone(v)
                                                 }
                                             }}/>}
                        </div>}
                    </div>

                    {!firstSettingsLink ? null : (
                        <Link
                            className="w-10 h-10 rounded-md flex items-center justify-center bg-white"
                            to={firstSettingsLink.path}
                        >
                            <img src={SettingIcon} alt=""/>
                        </Link>
                    )}

                    {accessUserManagement && (
                        <Link
                            to={managementLink}
                            className="w-10 h-10 rounded-md flex items-center justify-center bg-white"
                        >
                            <img src={PublicIcon} alt=""/>
                        </Link>
                    )}
                    <Link
                        className="w-10 h-10 rounded-md flex items-center justify-center bg-blue font-semibold text-md"
                        to={`management/users/edit/${userContext.sub}`}
                    >
                        {`${userFirstNameInitial}${userLastNameInitial}`}
                    </Link>

                    <button
                        className="w-10 h-10 rounded-md flex items-center justify-center bg-white font-semibold text-xl"
                        onClick={handleLogout}
                    >
                        <LogoutIcon style={{color: "grey"}}/>
                    </button>
                </div>
            </div>
        </header>
    );
}

export default Header;
